/**
 * The "Booking" rail card — a pre-book order's or quote's delivery window and
 * the season it books (spec 16, design 18 S-PB2/S-PB4).
 *
 * Design 18 says these rows go in a "meta card". There isn't one: order detail's
 * rail is exactly Buyer + Payment, and the meta lives in a plain sub-line `<p>`
 * with no room for a labelled pair, an `(archived)` suffix, or an overdue tint.
 * So this is a new prebook-only card instead — which also means an at-once
 * screen renders byte-identically to before (AC5), because nothing is added.
 *
 * Placed **first** in the rail by its callers: it is the thing that makes this
 * order unusual, and the reader is scanning for it.
 */
import { Card, CardBody, CardHeader } from "@flint/ui";
import { Link } from "@tanstack/react-router";
import { deliveryWindow } from "./date-window.ts";

export interface BookingCardProps {
  kind: "at_once" | "prebook";
  deliveryStartsOn: string | null;
  deliveryEndsOn: string | null;
  sourceCollectionId: string | null;
  sourceCollectionName: string | null;
  sourceCollectionStatus: "active" | "archived" | null;
  /**
   * Whether the promise has been missed. Passed in rather than derived: the
   * server already decided it for the list's Past-dispatch pill, and deciding it
   * twice is how the two end up disagreeing. Quotes pass `false` — a quote makes
   * no delivery promise until it becomes an order.
   */
  overdue?: boolean;
}

export function BookingCard({
  kind,
  deliveryStartsOn,
  deliveryEndsOn,
  sourceCollectionId,
  sourceCollectionName,
  sourceCollectionStatus,
  overdue = false,
}: BookingCardProps) {
  if (kind !== "prebook") return null;

  return (
    <Card>
      <CardHeader>
        <h2 className="text-sm font-semibold text-ink">Booking</h2>
      </CardHeader>
      <CardBody>
        <dl className="grid gap-2">
          <div className="flex items-center justify-between text-sm">
            <dt className="text-muted">Delivery window</dt>
            <dd
              className={
                overdue
                  ? "font-mono tabular-nums text-bad"
                  : "font-mono tabular-nums text-ink"
              }
            >
              {deliveryWindow(deliveryStartsOn, deliveryEndsOn)}
            </dd>
          </div>
          <div className="flex items-center justify-between text-sm">
            <dt className="text-muted">Season</dt>
            <dd className="text-right">
              {sourceCollectionId && sourceCollectionName ? (
                <Link
                  to="/store/admin/collections/$collectionId"
                  params={{ collectionId: sourceCollectionId }}
                  className="text-ink hover:text-sky-700"
                >
                  {sourceCollectionName}
                  {/* Admin surfaces never hide an archived collection (spec 15
                      R3) — the link still resolves, it just says so. */}
                  {sourceCollectionStatus === "archived" ? (
                    <span className="ml-1 text-xs text-muted">(archived)</span>
                  ) : null}
                </Link>
              ) : (
                <span className="text-muted">—</span>
              )}
            </dd>
          </div>
        </dl>
      </CardBody>
    </Card>
  );
}
